/**
 * BatchInvoiceImport - Import batch de factures clients (migration)
 *
 * Réutilise BatchUploadZone + BatchReviewLayout + useBatchReview (DRY).
 * Seul le contenu des cartes est spécifique aux factures.
 *
 * Design System: Dashboard context (text-gray-900, bg-white/70, teal-600)
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Calendar,
  Euro,
  Hash,
  ArrowRight,
  Upload,
} from 'lucide-react';
import { BatchUploadZone } from '@/features/expenses/components/BatchUploadZone';
import { BatchReviewLayout } from '@/features/expenses/components/BatchReviewLayout';
import { useBatchOCR } from '@/features/expenses/hooks/useBatchOCR';
import { useBatchReview } from '@/features/expenses/hooks/useBatchReview';
import { useBatchImportInvoices, type BatchInvoiceData, type BatchInvoiceLineItem } from '../hooks/useBatchImportInvoices';

function extractLineItems(ocrData: Record<string, unknown>): BatchInvoiceLineItem[] | undefined {
  const rawLines = ocrData.lines || ocrData.line_items || ocrData.lineItems;
  if (!Array.isArray(rawLines) || rawLines.length === 0) return undefined;

  return rawLines
    .filter((line: Record<string, unknown>) => line.description || line.label)
    .map((line: Record<string, unknown>) => ({
      description: (line.description || line.label || '') as string,
      quantity: Number(line.quantity || line.qty || 1),
      unitPrice: Number(line.unit_price || line.unitPrice || line.price || 0),
      taxPercent: Number(line.tax_percent || line.taxPercent || line.vat || 20),
    }));
}

function extractInvoiceData(ocrData: Record<string, unknown> | null, filename: string): BatchInvoiceData {
  if (!ocrData) {
    return {
      clientName: '',
      invoiceNumber: '',
      issueDate: new Date().toISOString().split('T')[0],
      totalHT: 0,
      taxRate: 20,
      status: 'PAID',
    };
  }

  const client = ocrData.client || ocrData.customer;
  const clientObj = typeof client === 'object' && client !== null ? client as Record<string, unknown> : null;

  return {
    clientName: clientObj?.name as string || clientObj?.company_name as string || ocrData.clientName as string || '',
    clientSiret: clientObj?.siret as string || ocrData.clientSiret as string || '',
    invoiceNumber: ocrData.invoice_number as string || '',
    issueDate: ocrData.invoice_date as string || new Date().toISOString().split('T')[0],
    dueDate: ocrData.due_date as string || undefined,
    totalHT: Number(ocrData.total_ht || ocrData.totalHt || 0),
    taxRate: Number(ocrData.tva_rate || ocrData.tvaRate || 20),
    totalTTC: Number(ocrData.total_ttc || ocrData.totalTtc || 0) || undefined,
    lineItems: extractLineItems(ocrData),
    status: 'PAID',
  };
}

interface BatchInvoiceImportProps {
  onClose: () => void;
}

export function BatchInvoiceImport({ onClose }: BatchInvoiceImportProps) {
  const { t } = useTranslation();
  const batchOCR = useBatchOCR();
  const batchImport = useBatchImportInvoices();
  const review = useBatchReview<BatchInvoiceData>(batchOCR.results, extractInvoiceData);

  const inputClasses = 'w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:ring-2 focus:ring-teal-500/50 focus:border-teal-500 transition-all';

  const hasResults = batchOCR.results.length > 0;

  const handleImportAll = useCallback(() => {
    const items = review.getValidatedItems();
    if (items.length === 0) return;
    batchImport.mutate(items, {
      onSuccess: () => { onClose(); batchOCR.reset(); },
    });
  }, [review, batchImport, onClose, batchOCR]);

  return (
    <div className="px-6 py-8 space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => { onClose(); batchOCR.reset(); }}
          className="w-10 h-10 flex items-center justify-center rounded-xl bg-white/50 hover:bg-white/80 text-gray-600 hover:text-gray-900 transition-all"
        >
          <ArrowRight className="w-5 h-5 rotate-180" />
        </button>
        <div className="flex items-center gap-4">
          <div className="w-14 h-14 bg-gradient-to-br from-teal-50 to-teal-100/50 rounded-2xl flex items-center justify-center shadow-lg shadow-teal-200/30">
            <Upload className="w-7 h-7 text-teal-600" />
          </div>
          <div>
            <h1 className="text-4xl font-light tracking-tight text-gray-900">
              {t('invoices.batchImport.title', 'Importer des factures')}
            </h1>
            <p className="text-gray-500 mt-1">
              {t('invoices.batchImport.subtitle', 'Importez vos anciennes factures depuis des PDF')}
            </p>
          </div>
        </div>
      </div>

      {/* Step 1: Upload */}
      {!hasResults && (
        <BatchUploadZone
          onFilesSelected={batchOCR.processFiles}
          isProcessing={batchOCR.isProcessing}
          progress={batchOCR.progress}
          total={batchOCR.total}
        />
      )}

      {/* Step 2: Review — utilise BatchReviewLayout (DRY) */}
      {hasResults && (
        <BatchReviewLayout<BatchInvoiceData>
          stats={review.stats}
          items={review.items}
          isCreating={batchImport.isPending}
          onBack={() => batchOCR.reset()}
          onValidateAllPending={review.validateAllPending}
          onCreateAll={handleImportAll}
          createLabel={t('invoices.batchImport.importN', 'Importer {{count}} facture(s)', { count: review.stats.validated })}
          onValidate={review.validateItem}
          onReject={review.rejectItem}
          onEdit={review.editItem}
          onConfirmEdit={review.confirmEdit}
          onCancelEdit={review.cancelEdit}
          renderCardContent={(item, index, isEditing) => {
            const data = item.editedData;
            return (
              <>
                <div className="flex items-center gap-2">
                  <Building2 className="w-4 h-4 text-gray-400" />
                  {isEditing ? (
                    <input type="text" value={data.clientName || ''} onChange={(e) => review.updateField(index, 'clientName', e.target.value)} className={inputClasses} placeholder="Nom du client" />
                  ) : (
                    <span className="text-sm text-gray-900 font-medium">{data.clientName || 'Client inconnu'}</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  <Hash className="w-4 h-4 text-gray-400" />
                  {isEditing ? (
                    <input type="text" value={data.invoiceNumber || ''} onChange={(e) => review.updateField(index, 'invoiceNumber', e.target.value)} className={inputClasses} placeholder="N° facture" />
                  ) : (
                    <span className="text-sm text-gray-600">{data.invoiceNumber || 'N° non détecté'}</span>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="flex items-center gap-2">
                    <Euro className="w-4 h-4 text-gray-400" />
                    {isEditing ? (
                      <input type="number" step="0.01" value={data.totalHT} onChange={(e) => review.updateField(index, 'totalHT', parseFloat(e.target.value) || 0)} className={inputClasses} />
                    ) : (
                      <span className="text-sm font-semibold text-gray-900">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(data.totalHT)} HT</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    {isEditing ? (
                      <input type="date" value={data.issueDate} onChange={(e) => review.updateField(index, 'issueDate', e.target.value)} className={inputClasses} />
                    ) : (
                      <span className="text-sm text-gray-600">{new Date(data.issueDate).toLocaleDateString('fr-FR')}</span>
                    )}
                  </div>
                </div>
              </>
            );
          }}
        />
      )}
    </div>
  );
}
